<script lang="ts">
	import { FULL_SITE_URL, SITE_NAME } from '$lib/statics';
	import { UrlUtil } from '$lib/utils/url.util';

	const LAST_UPDATED = 'February 2026';

	const meta = {
		title: `Terms of Use - ${SITE_NAME}`,
		description: `Terms and conditions for using ${SITE_NAME}.`,
		canonicalUrl: `${FULL_SITE_URL}/${UrlUtil.toTermsOfUse().href}`,
		imageUrl: UrlUtil.getStaticFileFullUrl('images', 'scenic-coastal-city-travel-destinations-brazil-banner.jpg')
	};
</script>

<svelte:head>
	<title>{meta.title}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={meta.canonicalUrl} />

	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:url" content={meta.canonicalUrl} />
	<meta property="og:image" content={meta.imageUrl} />
	<meta property="og:type" content="website" />

	<meta property="twitter:title" content={meta.title} />
	<meta property="twitter:description" content={meta.description} />
	<meta property="twitter:url" content={meta.canonicalUrl} />
	<meta property="twitter:image" content={meta.imageUrl} />
	<meta property="twitter:card" content="summary_large_image" />
</svelte:head>

<div class="container mx-auto min-h-screen max-w-4xl px-4 py-16 text-base-content md:py-24 lg:px-0">
	<header class="mb-12 border-b border-base-content/10 pb-8">
		<h1 class="mb-4 text-4xl md:text-5xl lg:text-6xl">Terms of Use</h1>
		<p class="text-md font-medium text-base-content/70">Last Updated: {LAST_UPDATED}</p>
	</header>

	<div class="flex flex-col gap-10 text-lg leading-relaxed font-light text-base-content/80">
		<section class="flex flex-col gap-3">
			<h2 class="text-2xl text-base-content">1. Acceptance of Terms</h2>
			<p>
				By accessing and using <strong>{SITE_NAME}</strong> ("the Website"), you accept and agree to be bound by these Terms of Use.
				If you do not agree to these terms, please do not use our Website.
			</p>
		</section>

		<section class="flex flex-col gap-3">
			<h2 class="text-2xl text-base-content">2. Nature of Information (No Warranties)</h2>
			<p>
				{SITE_NAME} is an informational travel guide. While we strive to provide accurate, inspiring, and up-to-date information regarding
				cities, attractions, and cultural experiences,
				<strong>we make no warranties or guarantees regarding the accuracy, completeness, or reliability of any content.</strong>
			</p>
			<p>
				Travel landscapes change rapidly. Museums close, restaurant prices increase, and transit schedules alter. Our content
				should be used for inspiration and planning purposes only. You are solely responsible for verifying operating hours, entry
				requirements, and safety conditions before traveling to any destination mentioned on the Website.
			</p>
		</section>

		<section class="flex flex-col gap-3">
			<h2 class="text-2xl text-base-content">3. Intellectual Property and Licensing</h2>
			<p>
				<strong>Images:</strong> The photographs and images displayed on {SITE_NAME} are primarily sourced from Wikimedia Commons and
				other open-license repositories. These images remain the intellectual property of their respective creators. We provide explicit
				attribution and links to the original creative licenses via embedded data (JSON-LD) and on-page citations where applicable.
			</p>
			<p>
				<strong>Text and Design:</strong> The curated itineraries, custom descriptions, database structures, and overall design
				and code of the Website are the exclusive property of {SITE_NAME}. You may not scrape, copy, reproduce, or redistribute
				our written content or interface designs without explicit written permission.
			</p>
		</section>

		<section class="flex flex-col gap-3">
			<h2 class="text-2xl text-base-content">4. Limitation of Liability</h2>
			<p>
				To the maximum extent permitted by law, {SITE_NAME} and its operators shall not be liable for any direct, indirect, incidental,
				consequential, or special damages arising out of or in any way connected with your access to or use of the Website.
			</p>
			<p>
				We are not responsible for any financial loss, injury, travel disruptions, or ruined vacations that result from relying on
				the information provided on our pages.
			</p>
		</section>

		<section class="flex flex-col gap-3">
			<h2 class="text-2xl text-base-content">5. Third-Party Links</h2>
			<p>
				Our Website contains links to third-party websites (e.g., YouTube, Wikimedia). These links are provided for your
				convenience. We have no control over the content of those sites and accept no responsibility for them or for any loss or
				damage that may arise from your use of them.
			</p>
		</section>

		<section class="flex flex-col gap-3">
			<h2 class="text-2xl text-base-content">6. Governing Law</h2>
			<p>
				These Terms of Use shall be governed by and construed in accordance with the laws of England and Wales. Any disputes
				arising under or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of England
				and Wales.
			</p>
		</section>

		<section class="flex flex-col gap-3">
			<h2 class="text-2xl font-bold text-base-content">7. Changes to these Terms</h2>
			<p>
				We reserve the right to modify these Terms of Use at any time. We will indicate that changes have been made by updating
				the "Last Updated" date at the top of this page. Your continued use of the Website after such changes constitutes your
				acceptance of the new terms.
			</p>
		</section>
	</div>
</div>
